import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GoalCompleteCelebrationProps {
  isOpen: boolean;
  onClose: () => void;
  goalTitle: string;
  completedCount: number;
}

export const GoalCompleteCelebration = ({
  isOpen,
  onClose,
  goalTitle,
  completedCount,
}: GoalCompleteCelebrationProps) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  const handleShare = () => {
    const text = `I just completed my goal "${goalTitle}"! ${completedCount} goals achieved so far. Building career momentum one goal at a time. #MomentumEQ #CareerGrowth`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const isAchiever = completedCount >= 3;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-primary/30 bg-gradient-to-b from-background to-background/95 overflow-hidden">
        <DialogTitle className="sr-only">Goal Completed!</DialogTitle>

        {/* Confetti Background */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-confetti-fall"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              >
                <div
                  className={cn(
                    "w-2 h-2 rounded-full",
                    i % 5 === 0 && "bg-primary",
                    i % 5 === 1 && "bg-accent",
                    i % 5 === 2 && "bg-warning",
                    i % 5 === 3 && "bg-success",
                    i % 5 === 4 && "bg-secondary"
                  )}
                  style={{
                    transform: `rotate(${Math.random() * 360}deg)`,
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        {/* Content */}
        <div className="flex flex-col items-center text-center py-8 px-4 relative z-10">
          {/* Animated Emoji */}
          <div className="relative mb-6">
            <span
              className="text-7xl animate-milestone-emoji block"
              role="img"
              aria-label="trophy"
            >
              🏆
            </span>
            <div className="absolute inset-0 blur-2xl opacity-50 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full" />
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-yellow-500 to-amber-600 bg-clip-text text-transparent animate-fade-in">
            Goal Complete!
          </h2>

          {/* Goal Name */}
          <p className="text-lg text-foreground font-medium mb-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            "{goalTitle}"
          </p>

          {/* Completed Count */}
          <div className="flex items-center gap-2 mb-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <span className="text-4xl font-bold text-foreground">{completedCount}</span>
            <span className="text-lg text-muted-foreground">{completedCount === 1 ? 'goal' : 'goals'} achieved!</span>
          </div>

          {/* Achiever Status */}
          {isAchiever && (
            <div className="mb-4 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                Goal Achiever status unlocked!
              </span>
            </div>
          )}

          {/* Subtitle */}
          <p className="text-muted-foreground mb-6 max-w-xs animate-fade-in" style={{ animationDelay: '0.5s' }}>
            Every completed goal brings you closer to the career you deserve.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Button
              variant="outline"
              onClick={handleShare}
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share on LinkedIn
            </Button>
            <Button onClick={onClose}>
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
